<script setup>
    import { ref, onMounted, computed, defineAsyncComponent } from 'vue';
    import router from '@/router';
    import { useDiaAbonadoSga } from '@/composables/front/sga/useDiaAbonadoSga';
    import { t } from '@/utils/i18n-helper';

    // Stepper y cuenta atrás, cargados estáticos como en el resto del checkout.
    import Stepper from '@/components/frontend/events/compra/components/Stepper.vue';
    import CuentaAtras from '@/components/frontend/events/compra/CuentaAtras';

    // Pasos pesados, en lazy.
    const SeleccionarAbonoDiaAbonadoSga = defineAsyncComponent(
        ()=> import('./SeleccionarAbonoDiaAbonadoSga.vue')
    );
    const PagoSuplementoComponent = defineAsyncComponent(
        ()=> import('@/modules/front/sga/suplemento/components/PagoSuplementoComponent.vue')
    );

    const props = defineProps({
        event: {
            type: Object,
            required: true,
        },
        sesion_id: {
            type: [Number, String],
            default: null,
        },
    });

    const stepper = ref(null);

    const {
        loading,
        error,
        abonos,
        comprando,
        cancelando,
        cartId,
        sessionId,
        totalPrice,
        price,
        priceManagement,
        eventName,
        jetId,
        formularioPago,
        isAuthenticated,
        cargarAbonos,
        comprar,
        cancelar,
    } = useDiaAbonadoSga();

    // Abono elegido cuando el usuario tiene más de uno.
    const abonoSeleccionado = ref(null);

    /**
     * Desglose que pinta PagoSuplementoComponent: partido, precio sin gastos,
     * gastos de gestión y total.
     */
    const resumenPago = computed(() => {
        if (!cartId.value) {
            return null;
        }

        return {
            eventName: eventName.value || props.event?.name || null,
            price: price.value,
            priceManagement: priceManagement.value,
            total: totalPrice.value,
        };
    });

    /**
     * Pasos del proceso. El abono se lista siempre, aunque el usuario tenga
     * uno solo: es el paso que explica con qué abono va a pagar.
     */
    const steps = computed(() => [
        {
            label: t('front.sga.dia_abonado.paso_seleccionar_abono'),
            description: t('front.sga.dia_abonado.selecciona_abono'),
            slot: 'pasoAbono',
        },
        {
            label: t('front.sga.dia_abonado.paso_resumen'),
            description: t('front.sga.dia_abonado.suplemento'),
            slot: 'pasoResumen',
        },
    ]);

    // Error al cancelar: se muestra en el resumen y NO se deja volver hasta
    // que el carrito se borre de verdad.
    const errorCancelacion = ref(null);

    /**
     * Cancela el carrito creado (si lo hay) para no dejarlo huérfano en SGA
     * y limpia la selección.
     *
     * Si el borrado falla NO se vuelve: el carrito sigue vivo en SGA, y al
     * comprar con otro abono el id local se sobrescribiría, perdiendo la única
     * referencia para cancelarlo. Se queda mostrando el error para poder
     * reintentar. Devuelve true solo si se puede continuar.
     */
    async function cancelarYSalir() {
        errorCancelacion.value = null;

        try {
            await cancelar();
        } catch (e) {
            errorCancelacion.value =
                e?.response?.data?.message || t('front.sga.dia_abonado.error_cancelar');
            return false;
        }

        abonoSeleccionado.value = null;
        return true;
    }

    async function volverAAbonos(changeStep) {
        if (!(await cancelarYSalir())) {
            return;
        }

        changeStep(true);
    }

    /**
     * Se agotó el tiempo de compra. `CuentaAtras` borra la sesión guardada,
     * pero no conoce el carrito SGA de este flujo: si no se cancela aquí
     * queda vivo y la siguiente compra perdería la referencia para
     * borrarlo. Se aplica la misma regla que en «Volver».
     */
    async function alVencerTiempo() {
        if (!(await cancelarYSalir())) {
            return;
        }

        stepper.value?.changeStep(false, true);
    }

    function irALogin() {
        const currentUrl = window.location.pathname + window.location.search + window.location.hash;

        router.push({
            name: 'login',
            query: { redirect: currentUrl !== '/' ? currentUrl : undefined },
        });
    }

    async function handleAbonoSelect(abono, changeStep) {
        abonoSeleccionado.value = abono;

        try {
            await comprar(abono, props.event.id);
            changeStep();
        } catch (e) {
            // El composable ya deja el mensaje en `error`.
        }
    }

    onMounted(async() => {
        // Sin sesión no hay abonos que listar: se manda al login y se vuelve
        // a esta misma URL.
        if (!isAuthenticated.value) {
            irALogin();
            return;
        }

        await cargarAbonos(props.event.id);
    });
</script>

<template>
    <div class="proceso-dia-abonado-sga">
        <CuentaAtras @tiempoTerminado="alVencerTiempo" />

        <section class="padding-vertical-content">
            <Stepper ref="stepper" :steps="steps">
                <!-- Selección de abono: siempre visible -->
                <template #pasoAbono="{ changeStep }">
                    <SeleccionarAbonoDiaAbonadoSga
                        :abonos="abonos"
                        :loading="loading"
                        :error="error"
                        :comprando="comprando"
                        :event-name="eventName || event?.name"
                        @select="(abono) => handleAbonoSelect(abono, changeStep)"
                    />
                </template>

                <!-- Resumen y pago del suplemento.
                     Se reutiliza el mismo componente que el perfil: pinta el
                     desglose (precio + gastos + total) y monta el TPV. Al
                     terminar navega a /compra-lfp/{session}/{n_pedido}. -->
                <template #pasoResumen="{ changeStep }">
                    <!-- Mismo ancho que el stepper (1200px) para que el
                         resumen quede alineado con la línea de pasos. -->
                    <div class="paso-resumen-dia-abonado">
                        <div v-if="error || errorCancelacion" class="alert alert-danger">
                            {{ errorCancelacion || error }}
                        </div>
                        <PagoSuplementoComponent
                            v-else
                            :jet-id="jetId"
                            :formulario-pago="formularioPago"
                            :session-id="sessionId"
                            :resumen="resumenPago"
                        />

                        <!-- Volver atrás: borra el carrito creado en SGA para
                             no dejarlo huérfano al cambiar de abono. -->
                        <div class="paso-resumen-dia-abonado__acciones">
                            <button
                                type="button"
                                class="button medium-button"
                                :disabled="cancelando"
                                @click="volverAAbonos(changeStep)"
                            >
                                <span v-if="cancelando">{{ $t('front.sga.dia_abonado.cancelando') }}</span>
                                <span v-else>{{ $t('front.sga.dia_abonado.volver') }}</span>
                            </button>
                        </div>
                    </div>
                </template>
            </Stepper>
        </section>
    </div>
</template>

<style scoped>
.proceso-dia-abonado-sga {
    min-height: 400px;
}

.paso-resumen-dia-abonado {
    /* El stepper de arriba se limita a 1200px: el resumen usa el mismo ancho
       para quedar alineado con la línea de pasos. */
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
}

.paso-resumen-dia-abonado__acciones {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #dee2e6;
}
</style>
